import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useLoanDiscount } from 'app/hooks/use-loan-discount';
import { useHasPartagePlusSubscription } from 'app/hooks/use-has-partage-plus-subscription';
import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat } from 'app/lib/date';
import { formatCurrency } from 'app/localization/i18n';
import { useUserCurrency } from 'app/network/user';
import { useTheme } from 'app/shopify';
import type { FirstLoanMessage } from 'app/types/chat';
import { LoanStatus } from 'app/types/loan';
import { isLoanDiscountActive } from 'app/utils/loan-discount';
import { IconChevronRight } from '@tabler/icons-react-native';
import React from 'react';
import { Pressable } from 'react-native';
import { Trans, useTranslation } from 'react-i18next';

export function FirstLoanMessageCard({ isLender, message }: { isLender: boolean; message: FirstLoanMessage }) {
  const { i18n, t } = useTranslation('one-channel');
  const navigation = useNavigation();
  const theme = useTheme();
  const userCurrency = useUserCurrency();
  const hasPartagePlusSubscription = useHasPartagePlusSubscription();
  const estimatedPrice = message.loan?.listing.estimatedPrice;
  const savedAmount =
    estimatedPrice === null || estimatedPrice === undefined
      ? undefined
      : formatCurrency(Math.round(estimatedPrice), userCurrency, 0, i18n.resolvedLanguage);
  const { discountDeadline } = useLoanDiscount();
  const discountDeadlineLabel =
    discountDeadline && isLoanDiscountActive(discountDeadline)
      ? localeAwareFormat(discountDeadline, t('dateFormat_MMMMd', { ns: 'common' }))
      : undefined;
  const loanStatus = message.loan?.status ?? message.data.loanStatus;
  const isFreeBorrowConsumed = loanStatus === LoanStatus.Returned || loanStatus === LoanStatus.Completed;
  const showCancellationNote = !isLender && !isFreeBorrowConsumed;
  const showFootnote = Boolean(discountDeadlineLabel) || showCancellationNote;
  const premiumTextStyle = { color: '#64466C' } as const;

  if (hasPartagePlusSubscription !== false) return null;

  return (
    <Column
      alignItems="center"
      borderRadius="radius.lg"
      style={{ backgroundColor: '#F6F7FF', gap: 20, padding: 24 }}
      width="100%"
    >
      <Column
        alignItems="center"
        borderRadius="radius.full"
        justifyContent="center"
        style={{ backgroundColor: '#CFC1E1', paddingHorizontal: 16, paddingVertical: 8 }}
      >
        <Text fontSize={24}>🎉</Text>
      </Column>
      <Column alignItems="center" gap="spacing.general.x3" width="100%">
        <Text color="text.primary" textAlign="center" variant="bodyXLargeMedium" width="100%">
          {isLender || savedAmount === undefined ? (
            t(isLender ? 'firstLoanPrompt.lending.title' : 'firstLoanPrompt.title')
          ) : (
            <Trans
              components={{ highlight: <Text style={premiumTextStyle} variant="bodyXLargeMedium" /> }}
              i18nKey="firstLoanPrompt.savedAmount"
              ns="one-channel"
              values={{ amount: savedAmount }}
            />
          )}
        </Text>
        <Text style={premiumTextStyle} textAlign="center" variant="bodyLargeMedium" width="100%">
          {t(isLender ? 'firstLoanPrompt.lending.description' : 'firstLoanPrompt.description')}
        </Text>
        <Text color="text.primary" textAlign="center" variant="bodyBase" width="100%">
          <Trans
            components={{ strong: <Text style={premiumTextStyle} variant="bodyBaseStrong" /> }}
            i18nKey={isLender ? 'firstLoanPrompt.lending.subscribeDescription' : 'firstLoanPrompt.subscribeDescription'}
            ns="one-channel"
          />
          {showFootnote ? (
            <Text style={premiumTextStyle} variant="bodyBaseStrong">
              *
            </Text>
          ) : null}
        </Text>
      </Column>
      <Column alignSelf="stretch" gap="spacing.general.x2">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('firstLoanPrompt.cta')}
          onPress={() => navigation.navigate('profile', { screen: 'payment', initial: false })}
          style={{
            alignItems: 'center',
            alignSelf: 'stretch',
            backgroundColor: '#D9DCFF',
            borderRadius: 1000,
            elevation: 2,
            flexDirection: 'row',
            gap: 8,
            justifyContent: 'center',
            minHeight: 48,
            paddingHorizontal: 24,
            paddingVertical: 12,
            shadowColor: theme.colors['shadow.strong'],
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 1,
            shadowRadius: 4,
          }}
        >
          <Text color="text.primary" variant="bodyBaseMedium">
            {t('firstLoanPrompt.cta')}
          </Text>
          <PartagePlusBadge variant="small" />
          <IconChevronRight color={theme.colors['icon.primary']} size={24} strokeWidth={1.5} />
        </Pressable>
        {showFootnote ? (
          <Text color="text.secondary" textAlign="center" variant="bodyCaption" width="100%">
            {discountDeadlineLabel ? (
              <Text color="text.secondary" variant="bodyCaptionStrong">
                *{t('firstLoanPrompt.discountDeadlineNote', { date: discountDeadlineLabel })}{' '}
              </Text>
            ) : showCancellationNote ? (
              '*'
            ) : null}
            {showCancellationNote ? t('firstLoanPrompt.note') : null}
          </Text>
        ) : null}
      </Column>
    </Column>
  );
}
